import { Injectable } from '@angular/core';
import BackgroundGeolocation from '@transistorsoft/capacitor-background-geolocation';
import { format, subHours } from 'date-fns';
import * as Sentry from '@sentry/angular';
import { environment } from 'src/environments/environment';
import { Capacitor } from '@capacitor/core';
import { StorageService } from 'src/app/services/storage.service';
import { TranslateService } from '@ngx-translate/core';
import { VehicleInspectionService } from './vehicle-inspection.service';
import { addDays } from 'date-fns';
import { LoggerService } from 'src/app/services/logger.service';
import { GeolocationService } from 'src/app/my-transfers/services/geolocation.service';
import { translatePermissionRationale } from 'src/app/shared/helpers/translate-permission-rationale.helper';

const TRACKING_SCHEDULE_STORAGE_KEY = 'current_tracking_schedule';

/**
 * The active tracking schedule, persisted so it can be re-armed on a cold app
 * launch. `BackgroundGeolocation.ready()` runs with the default `reset: true`,
 * which resets the config to whatever is passed to `ready()` — wiping any
 * `schedule`/`extras` previously applied via `setConfig()`.
 */
interface PersistedTrackingSchedule {
  schedule: string;
  referenceKey: string;
  trackingId: string;
  expiresAt: string;
}

@Injectable({
  providedIn: 'root',
})
export class VehicleInspectionTrackingService {
  private logger = new LoggerService(VehicleInspectionTrackingService.name);

  // Tracks whether BackgroundGeolocation.ready() has run this session. ready()
  // is only called in initTracking() (normally at app startup), and the plugin
  // ignores start()/setConfig()/startSchedule() until it has run. If the user
  // rejected the background-location disclosure at launch,scheduleTransferTracking()
  // uses this to re-init before scheduling once the disclosure is finally accepted in the pickup flow.
  private hasCalledReady = false;

  constructor(
    private storageService: StorageService,
    private translateService: TranslateService,
    private vehicleInspectionService: VehicleInspectionService,
    private geolocationService: GeolocationService,
  ) {}

  async initTracking() {
    if (!Capacitor.isNativePlatform()) {
      return null;
    }

    // Fetch token BEFORE ready() so the NSURLSession is created with the correct
    // Authorization header from the start. On iOS, setConfig() updates the stored
    // config but not the already-created NSURLSession, so any HTTP request triggered
    // by start() would go out without the header if it's not included here.
    const token = await this.vehicleInspectionService.currentToken();

    const state = await BackgroundGeolocation.ready({
      desiredAccuracy: BackgroundGeolocation.DESIRED_ACCURACY_HIGH,
      distanceFilter: 3000,
      disableElasticity: true,
      stopTimeout: 1,
      debug: false,
      logLevel: BackgroundGeolocation.LOG_LEVEL_VERBOSE,
      stopOnTerminate: false,
      startOnBoot: true,
      enableHeadless: true,
      autoSync: true,
      batchSync: true,
      maxBatchSize: 5,
      autoSyncThreshold: 5,
      url: `${environment.vehicleInspectionUrl}/v1/tracking/`,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    this.hasCalledReady = true;

    BackgroundGeolocation.onHttp((response) => {
      if (!response.success) {
        this.logger.error({
          method: 'initTracking',
          identifier: 'movacarpro_tracking_http_error',
          message: 'http request failed',
          value: {
            status: response.status,
            response: response.responseText,
          },
        });
      }
    });

    await this.reArmPersistedSchedule();

    return state;
  }

  // ready() wiped any previously-configured schedule.
  // If a tracking window is still valid, re-arm it so this launch resumes the
  // schedule rather than dropping it. Critical for iOS, where the app may be
  // relaunched in the background after being killed.
  private async reArmPersistedSchedule(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const persisted: PersistedTrackingSchedule | null =
      await this.storageService.get(TRACKING_SCHEDULE_STORAGE_KEY);

    if (!persisted?.schedule) {
      return;
    }

    const expiresAt: number = new Date(persisted.expiresAt).getTime();

    console.log('persisted', persisted);

    if (!Number.isNaN(expiresAt) && expiresAt <= Date.now()) {
      this.logger.info({
        method: 'reArmPersistedSchedule',
        identifier: 'movacarpro_tracking_schedule_expired',
        message: 'persisted schedule expired, clearing',
        value: {
          referenceKey: persisted.referenceKey,
          expiresAt: persisted.expiresAt,
        },
      });

      await this.storageService.delete(TRACKING_SCHEDULE_STORAGE_KEY);

      return;
    }

    try {
      await BackgroundGeolocation.setConfig({
        schedule: [persisted.schedule],
        extras: {
          reference: persisted.referenceKey,
          trackingId: persisted.trackingId,
        },
      });

      await BackgroundGeolocation.startSchedule();

      this.logger.info({
        method: 'reArmPersistedSchedule',
        identifier: 'movacarpro_tracking_schedule_rearmed',
        message: 're-armed persisted schedule after ready()',
        value: {
          referenceKey: persisted.referenceKey,
          schedule: persisted.schedule,
        },
      });
    } catch (error) {
      Sentry.captureException(error);

      this.logger.error({
        method: 'reArmPersistedSchedule',
        identifier: 'movacarpro_tracking_schedule_rearm_failed',
        message: 'failed to re-arm persisted schedule',
        value: error,
      });
    }
  }

  /**
   * Start the tracking for a transfer
   * @param startTimestamp the planned dropoff. The schedule starts 3 hours earlier (applied in formatScheduleDates).
   * @param endTimestamp the latest dropoff. The schedule ends 7 days later (applied in formatScheduleDates).
   * @param referenceKey the reference key of the transfer
   */
  async scheduleTransferTracking(
    startTimestamp: string,
    endTimestamp: string,
    referenceKey: string,
    trackingId: string,
  ) {
    if (!Capacitor.isNativePlatform()) {
      return null;
    }

    // ready() runs in initTracking(), normally at app startup. If the user
    // rejected the background-location disclosure at launch, initTracking() was
    // skipped and ready() never ran, so the start()/setConfig()/startSchedule()
    // calls below would silently no-op and tracking would never be scheduled.
    // By the time we reach here the disclosure has been accepted (the pickup
    // flow requires it), so initialise the plugin now before scheduling.
    if (!this.hasCalledReady) {
      this.logger.info({
        method: 'scheduleTransferTracking',
        identifier: 'movacarpro_tracking_schedule_ready_missing',
        message: 'ready() not yet called, running initTracking before schedule',
        value: { referenceKey },
      });

      await this.initTracking();
    }

    const existingSchedule: PersistedTrackingSchedule | null =
      await this.storageService.get(TRACKING_SCHEDULE_STORAGE_KEY);

    if (existingSchedule?.referenceKey === referenceKey) {
      this.logger.info({
        method: 'scheduleTransferTracking',
        identifier: 'movacarpro_tracking_schedule_already_initialised',
        message: 'tracking already initialised, skipping re-init',
        value: { referenceKey },
      });

      return null;
    }

    this.logger.info({
      method: 'scheduleTransferTracking',
      identifier: 'movacarpro_tracking_schedule_start',
      message: 'start',
      value: { startTimestamp, endTimestamp, referenceKey },
    });

    const { title, message, positiveAction, negativeAction } =
      translatePermissionRationale(this.translateService);

    const token = await this.vehicleInspectionService.authenticate();

    await BackgroundGeolocation.setConfig({
      backgroundPermissionRationale: {
        title,
        message,
        positiveAction,
        negativeAction,
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
      extras: {
        reference: referenceKey,
        trackingId: trackingId,
      },
    });

    const { scheduleStartTimestamp, scheduleEndTimestamp, scheduleEndIso } =
      this.formatScheduleDates(startTimestamp, endTimestamp);

    const scheduleEntry = `${scheduleStartTimestamp} ${scheduleEndTimestamp}`;

    const hasAlwaysAuthorization: boolean =
      await this.geolocationService.hasAlwaysAuthorization();

    if (!hasAlwaysAuthorization) {
      this.logger.warn({
        method: 'scheduleTransferTracking',
        identifier: 'movacarpro_tracking_schedule_missing_always',
        message: 'scheduled without Always authorization',
        value: { referenceKey },
      });

      Sentry.captureMessage(
        'scheduleTransferTracking invoked without Always authorization',
      );
    }

    // It is needed to start and stop to get permissions
    await BackgroundGeolocation.start();
    await BackgroundGeolocation.getCurrentPosition({});
    await BackgroundGeolocation.stop();

    await BackgroundGeolocation.setConfig({
      schedule: [scheduleEntry],
    });

    await BackgroundGeolocation.startSchedule();

    const persistedSchedule: PersistedTrackingSchedule = {
      schedule: scheduleEntry,
      referenceKey,
      trackingId,
      expiresAt: scheduleEndIso,
    };

    await this.storageService.set(
      TRACKING_SCHEDULE_STORAGE_KEY,
      persistedSchedule,
    );

    let syncResult: object[] | null = null;
    try {
      syncResult = await BackgroundGeolocation.sync();
      this.logger.info({
        method: 'scheduleTransferTracking',
        identifier: 'movacarpro_tracking_schedule_sync_result',
        message: 'sync result',
        value: syncResult,
      });
    } catch (e) {
      this.logger.warn({
        method: 'scheduleTransferTracking',
        identifier: 'movacarpro_tracking_schedule_sync_busy',
        message: 'sync skipped (busy)',
        value: e,
      });
    }

    return syncResult;
  }

  /**
   * To avoid re-confirming the planned dropoff every time pickup
   * documentation is (re-)started: once tracking has been scheduled the dropoff
   * is locked in, so re-prompting on every re-entry of the pickup page is noise.
   * Backed by the same `current_tracking_schedule` key `scheduleTransferTracking`
   * writes, so it survives app restarts.
   */
  async isTrackingScheduledForTransfer(referenceKey: string): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      return false;
    }

    const persisted: PersistedTrackingSchedule | null =
      await this.storageService.get(TRACKING_SCHEDULE_STORAGE_KEY);

    return persisted?.referenceKey === referenceKey;
  }

  async stopTrackingTransfer() {
    if (!Capacitor.isNativePlatform()) {
      return null;
    }

    await BackgroundGeolocation.stopSchedule();
    await BackgroundGeolocation.start();
    await BackgroundGeolocation.getCurrentPosition({});
    await BackgroundGeolocation.stop();

    const token = await this.vehicleInspectionService.authenticate();

    await BackgroundGeolocation.setConfig({
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    let syncResult: object[] | null = null;
    try {
      syncResult = await BackgroundGeolocation.sync();
      this.logger.info({
        method: 'stopTrackingTransfer',
        identifier: 'movacarpro_tracking_stop_sync_result',
        message: 'sync result',
        value: syncResult,
      });
    } catch (e) {
      this.logger.warn({
        method: 'stopTrackingTransfer',
        identifier: 'movacarpro_tracking_stop_sync_busy',
        message: 'sync skipped (busy)',
        value: e,
      });
    } finally {
      await this.storageService.delete(TRACKING_SCHEDULE_STORAGE_KEY);
    }

    return syncResult;
  }

  formatScheduleDates(startTimestamp: string, endTimestamp: string) {
    const startDate = subHours(new Date(startTimestamp), 3);
    const endDate = addDays(new Date(endTimestamp), 7);

    const scheduleStartTimestamp = format(startDate, 'yyyy-MM-dd-HH:mm');
    const scheduleEndTimestamp = format(endDate, 'yyyy-MM-dd-HH:mm');

    return {
      scheduleStartTimestamp,
      scheduleEndTimestamp,
      scheduleEndIso: endDate.toISOString(),
    };
  }
}
